import React, { useState } from 'react';
import { MAX_VISIBLE_PARTICIPANTS, REP_TONE_DIMENSIONS } from '../../config/participants';
import { getBuyingSignalColor, MultiPartyCustomer, MultiPartyInsights as MultiPartyData, MultiPartyRep } from '../adherence/types';
import { ProgressBar, scoreTone } from '../ui/ProgressBar';
import { engagementLabel, inferredRoleLabel, joinNames, signalText } from './multiPartyFormat';

/**
 * Everyone on the call, side by side (callInsights.participants, written for
 * every call). Lives in the Participants accordion of Call Insights → Details,
 * open by default on multi-party calls; the Summary tab carries a compact
 * digest (ParticipantsSummaryCard). Per-rep numbers are coaching views for this call: the
 * call's quality score and all analytics stay with the team and the owner rep.
 */
interface MultiPartyInsightsProps {
  data: MultiPartyData;
}

const REP_SCORE_NOTE =
  'Per-rep scores are for coaching on this call. The call quality score and all analytics stay with the team.';

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <h4 className="text-xs font-bold uppercase tracking-wider text-ink-muted mb-2">{children}</h4>
);

/** A list that shows the first MAX_VISIBLE_PARTICIPANTS and expands on request. */
function useExpandable<T>(items: T[]): { visible: T[]; toggle: React.ReactNode } {
  const [expanded, setExpanded] = useState(false);
  const hidden = items.length - MAX_VISIBLE_PARTICIPANTS;
  const visible = expanded || hidden <= 0 ? items : items.slice(0, MAX_VISIBLE_PARTICIPANTS);
  const toggle =
    hidden > 0 ? (
      <button type="button" onClick={() => setExpanded(!expanded)}
              className="mt-2 text-xs font-medium text-signature hover:text-signature-strong">
        {expanded ? 'Show fewer' : `Show ${hidden} more`}
      </button>
    ) : null;
  return { visible, toggle };
}

const ScoreCell: React.FC<{ value: number | null; negative?: boolean }> = ({ value, negative = false }) =>
  value === null || value === undefined ? (
    <span className="text-xs text-ink-muted">—</span>
  ) : (
    <div className="flex items-center gap-2">
      <ProgressBar value={value} tone={scoreTone(value, !negative)} className="flex-1" />
      <span className="w-10 text-right text-xs text-ink-secondary">{value}%</span>
    </div>
  );

const RepComparison: React.FC<{ reps: MultiPartyRep[] }> = ({ reps }) => {
  const { visible, toggle } = useExpandable(reps);
  return (
    <section>
      <SectionTitle>Salespeople</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th className="text-left text-xs font-semibold text-ink-muted pb-2 pr-3">&nbsp;</th>
              {visible.map((rep) => (
                <th key={rep.participantId} className="text-left text-xs font-semibold text-ink pb-2 pr-3 min-w-[140px]">
                  {rep.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-1 pr-3 text-xs text-ink-secondary">Talk share</td>
              {visible.map((rep) => (
                <td key={rep.participantId} className="py-1 pr-3">
                  <div className="flex items-center gap-2">
                    <ProgressBar value={rep.talkPct} tone="signature" className="flex-1" />
                    <span className="w-10 text-right text-xs text-ink-secondary">{rep.talkPct}%</span>
                  </div>
                </td>
              ))}
            </tr>
            {REP_TONE_DIMENSIONS.map((dim) => (
              <tr key={dim.key}>
                <td className="py-1 pr-3 text-xs text-ink-secondary">{dim.label}</td>
                {visible.map((rep) => (
                  <td key={rep.participantId} className="py-1 pr-3">
                    <ScoreCell value={rep.tone?.[dim.key] ?? null} negative={dim.negative} />
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="py-1 pr-3 text-xs text-ink-secondary">Concern handling</td>
              {visible.map((rep) => (
                <td key={rep.participantId} className="py-1 pr-3 text-xs text-ink-secondary">
                  {rep.concernsHandled > 0
                    ? `${rep.concernsHandled} handled, ${rep.handlingScore === null || rep.handlingScore === undefined ? '—' : `${rep.handlingScore}%`}`
                    : 'None handled'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div className="mt-4 pt-4 border-t border-hairline space-y-2">
        {visible.map((rep) => (
          <div key={rep.participantId} className="text-sm">
            <span className="font-semibold text-ink">{rep.name}: </span>
            <span className="text-ink-secondary">
              {[rep.behaviour, rep.playbookContribution].filter(Boolean).join(' ') || 'No read available.'}
            </span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-ink-muted">{REP_SCORE_NOTE}</p>
      {toggle}
    </section>
  );
};

const TalkShare: React.FC<{ reps: MultiPartyRep[]; customers: MultiPartyCustomer[] }> = ({ reps, customers }) => {
  const everyone = [
    ...reps.map((rep) => ({ ...rep, role: 'Salesperson' })),
    ...customers.map((customer) => ({ ...customer, role: 'Customer' })),
  ].sort((a, b) => b.talkPct - a.talkPct);
  const { visible, toggle } = useExpandable(everyone);
  return (
    <section>
      <SectionTitle>Talk share</SectionTitle>
      <ul className="space-y-1.5">
        {visible.map((person) => (
          <li key={person.participantId} className="flex items-center gap-3">
            <span className="w-40 truncate text-sm text-ink">{person.name}</span>
            <span className="w-24 text-xs text-ink-muted">{person.role}</span>
            <ProgressBar value={person.talkPct} tone={person.role === 'Salesperson' ? 'signature' : 'info'} className="flex-1" />
            <span className="w-10 text-right text-xs text-ink-secondary">{person.talkPct}%</span>
          </li>
        ))}
      </ul>
      {toggle}
    </section>
  );
};

const CustomerCard: React.FC<{ customer: MultiPartyCustomer }> = ({ customer }) => (
  <div className="rounded-lg border border-hairline bg-white p-3">
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold text-sm text-ink">{customer.name}</span>
      <span className="text-xs bg-surface-subtle text-ink-secondary px-2 py-0.5 rounded">
        {inferredRoleLabel(customer.inferredRole)}
      </span>
      {customer.buyingSignal && (
        <span className={`text-xs px-2 py-0.5 rounded capitalize ${getBuyingSignalColor(customer.buyingSignal)}`}>
          {signalText(customer.buyingSignal)}
        </span>
      )}
      {engagementLabel(customer.engagement) && (
        <span className="text-xs text-ink-muted">{engagementLabel(customer.engagement)}</span>
      )}
      <span className="text-xs text-ink-muted">{customer.talkPct}% of the talk</span>
    </div>
    {customer.roleEvidence && <p className="mt-1 text-xs text-ink-muted">{customer.roleEvidence}</p>}
    {customer.behaviour && <p className="mt-2 text-sm text-ink">{customer.behaviour}</p>}
    {(customer.painPointsVolunteered?.length ?? 0) > 0 && (
      <p className="mt-2 text-xs text-ink-secondary">
        <span className="font-semibold">Pain points they raised: </span>
        {customer.painPointsVolunteered.join('; ')}
      </p>
    )}
    {(customer.concernsRaised?.length ?? 0) > 0 && (
      <p className="mt-1 text-xs text-ink-secondary">
        <span className="font-semibold">Concerns they raised: </span>
        {customer.concernsRaised.join('; ')}
      </p>
    )}
  </div>
);

const Customers: React.FC<{ customers: MultiPartyCustomer[] }> = ({ customers }) => {
  const { visible, toggle } = useExpandable(customers);
  return (
    <section>
      <SectionTitle>Customers</SectionTitle>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {visible.map((customer) => <CustomerCard key={customer.participantId} customer={customer} />)}
      </div>
      {toggle}
    </section>
  );
};

const MultiPartyInsights: React.FC<MultiPartyInsightsProps> = ({ data }) => {
  const reps = data?.reps ?? [];
  const customers = data?.customers ?? [];
  const minor = data?.minorParticipants ?? [];
  if (reps.length === 0 && customers.length === 0) return null;
  return (
    <div className="space-y-5 divide-y divide-hairline [&>*+*]:pt-5" data-testid="multi-party-insights">
      {reps.length > 0 && <RepComparison reps={reps} />}
      <TalkShare reps={reps} customers={customers} />
      {customers.length > 0 && <Customers customers={customers} />}
      {minor.length > 0 && (
        <p className="text-xs text-ink-muted">
          Also on the call briefly: {joinNames(minor.map((person) => person?.name).filter(Boolean) as string[])}
        </p>
      )}
    </div>
  );
};

export default MultiPartyInsights;
